import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate, useSearchParams } from 'react-router'
import { gql } from 'graphql-request'
import Layout from '@/components/Layout/Layout'
import { Tabs } from '@/components/Common/Tabs'
import { useStores } from '@/components/StoreProvider'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Building2, Users, CreditCard, Package, AlertTriangle, Target, MessageSquare, BarChart3, Settings, Receipt, Sparkles } from 'lucide-react'
import OrganisationSidebar from './components/OrganisationSidebar'
import SubscriptionsTab from './components/SubscriptionsTab'
import UsersTab from './components/UsersTab'
import ModulesTab from './components/ModulesTab'
import B2cModulesTab from './components/B2cModulesTab'
import TargetsTab from './components/TargetsTab'
import DangerTab from './components/DangerTab'
import ChatsTab from './components/ChatsTab'
import UserTargetsTab from './components/UserTargetsTab'
import SettingsTab from './components/SettingsTab'
import AvatarsTab from '@/components/Admin/AvatarsTab'
import ModulesView from '@/pages/Modules'
import UsersView from '@/pages/Users'
import PaymentAccountsView from '@/pages/PaymentAccounts'
import PlanCardsView from '@/pages/PlanCards'
import ActivityView from './components/ActivityView'
import PurchasesTab from './components/PurchasesTab'
import PlansTab from './components/PlansTab'

export type OrganisationKind = 'b2b' | 'b2c'

export interface Organisation {
  id: number
  name: string
  description: string | null
  kind: OrganisationKind
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface Module {
  id: number
  name: string
  coachFlowId?: number
  roleplayFlowId?: number
  reviewPromptPayloadId?: number
  timerDuration?: number
  defaultRemainingTime?: number
  reviewPageSize?: number
  microfankProjectId?: number
}

export interface Subscription {
  id: number
  organisationId: number
  moduleId: number
  module: Module | null
  numUsers: number
  numTokens: number
  numSessions: number
  numMinutes: number
  dueDate: string
  active: boolean
  /** Users assigned to this subscription. Seats belong to a subscription, not to its module. */
  userCount?: number
  createdAt: string
  updatedAt: string
}

export interface OrganisationUser {
  id: number
  email: string
  firstName: string | null
  lastName: string | null
  isActive: boolean
  isCoach: boolean
  createdAt: string
  updatedAt: string
}

export interface OrganisationWithDetails extends Organisation {
  users: OrganisationUser[]
  activeSubscriptions: Subscription[]
  lastInactiveSubscription: Subscription | null
  onboardingVideo1Url?: string | null
  onboardingVideo2Url?: string | null
  /** B2C only: the partner's Stripe account, the platform's cut, and whether a
   *  purchase could happen right now on this server. */
  paymentAccountId?: number | null
  applicationFeeBps?: number | null
  paymentsUsable?: boolean
}

const GET_ORGANISATIONS = gql`
  query getOrganisations {
    organisations: getOrganisations {
      id
      name
      description
      kind
      isActive
    }
  }
`

const GET_ORGANISATION_DETAILS = gql`
  query getOrganisationsWithSubscriptions($organisationId: Int) {
    getOrganisationsWithSubscriptions(organisationId: $organisationId) {
      id
      name
      description
      kind
      isActive
      createdAt
      updatedAt
      onboardingVideo1Url
      onboardingVideo2Url
      paymentAccountId
      applicationFeeBps
      paymentsUsable
      users {
        id
        email
        firstName
        lastName
        isActive
        isCoach
        createdAt
        updatedAt
      }
      activeSubscriptions {
        id
        organisationId
        moduleId
        module {
          id
          name
        }
        numUsers
        numTokens
        numSessions
        numMinutes
        dueDate
        active
        userCount
        createdAt
        updatedAt
      }
      lastInactiveSubscription {
        id
        organisationId
        moduleId
        module {
          id
          name
        }
        numUsers
        numTokens
        numSessions
        numMinutes
        dueDate
        active
        userCount
        createdAt
        updatedAt
      }
    }
  }
`

const GET_MODULES = gql`
  query getModules {
    modules: getModules {
      id
      name
      coachFlowId
      roleplayFlowId
      reviewPromptPayloadId
      timerDuration
      defaultRemainingTime
      reviewPageSize
      microfankProjectId
    }
  }
`

const DashboardPage: React.FC = observer(() => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { userStore } = useStores()
  const [organisations, setOrganisations] = useState<Organisation[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'subscriptions')
  const [avatarsView, setAvatarsView] = useState<boolean>(searchParams.get('view') === 'avatars')
  const [modulesView, setModulesView] = useState<boolean>(searchParams.get('view') === 'modules')
  const [usersView, setUsersView] = useState<boolean>(searchParams.get('view') === 'users')
  const [paymentsView, setPaymentsView] = useState<boolean>(searchParams.get('view') === 'payments')
  const [planCardsView, setPlanCardsView] = useState<boolean>(searchParams.get('view') === 'plan-cards')
  const [activityView, setActivityView] = useState<boolean>(searchParams.get('view') === 'activity')

  // A link can change ?view= without going through a handler (the Users overview links
  // to Activity), and so can Back/Forward: keep the view flags in step with the URL.
  const viewParam = searchParams.get('view')
  useEffect(() => {
    setAvatarsView(viewParam === 'avatars')
    setModulesView(viewParam === 'modules')
    setUsersView(viewParam === 'users')
    setPaymentsView(viewParam === 'payments')
    setPlanCardsView(viewParam === 'plan-cards')
    setActivityView(viewParam === 'activity')
  }, [viewParam])
  const [orgDetails, setOrgDetails] = useState<OrganisationWithDetails | null>(null)
  const [modules, setModules] = useState<Module[]>([])
  const [loading, setLoading] = useState(true)
  const [detailsLoading, setDetailsLoading] = useState(false)

  // Check if user is admin (has role_id = 1)
  useEffect(() => {
    if (!userStore.isAdmin) {
      navigate('/')
    }
  }, [userStore.isAdmin, navigate])

  const refreshModules = async () => {
    try {
      const modulesResponse = await authorizedGraphQLRequest<{ modules: Module[] }>(GET_MODULES)
      setModules(modulesResponse.modules || [])
    } catch (error) {
      console.error('Failed to refresh modules:', error)
    }
  }

  // Fetch organisations list
  useEffect(() => {
    const fetchOrganisations = async () => {
      try {
        setLoading(true)
        const [orgsResponse, modulesResponse] = await Promise.all([
          authorizedGraphQLRequest<{ organisations: Organisation[] }>(GET_ORGANISATIONS),
          authorizedGraphQLRequest<{ modules: Module[] }>(GET_MODULES),
        ])
        setOrganisations(orgsResponse.organisations || [])
        setModules(modulesResponse.modules || [])

        // Restore org from URL param, or select first by default
        const orgParam = searchParams.get('org')
        const orgs = orgsResponse.organisations || []
        if (orgParam && orgs.some((o: Organisation) => o.id === Number(orgParam))) {
          setSelectedOrgId(Number(orgParam))
        } else if (orgs.length > 0) {
          setSelectedOrgId(orgs[0].id)
        }
      } catch (error) {
        console.error('Failed to fetch organisations:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchOrganisations()
  }, [])

  // Fetch details when selected org changes
  useEffect(() => {
    const fetchOrgDetails = async () => {
      if (!selectedOrgId) return

      try {
        setDetailsLoading(true)
        const response = await authorizedGraphQLRequest<{
          getOrganisationsWithSubscriptions: OrganisationWithDetails[]
        }>(GET_ORGANISATION_DETAILS, { organisationId: selectedOrgId })

        if (response.getOrganisationsWithSubscriptions?.length > 0) {
          setOrgDetails(response.getOrganisationsWithSubscriptions[0])
        }
      } catch (error) {
        console.error('Failed to fetch organisation details:', error)
      } finally {
        setDetailsLoading(false)
      }
    }

    fetchOrgDetails()
  }, [selectedOrgId])

  const handleOrgSelect = (orgId: number) => {
    setSelectedOrgId(orgId)
    setSearchParams((prev) => {
      prev.set('org', String(orgId))
      return prev
    })
  }

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    setSearchParams((prev) => {
      prev.set('tab', tab)
      return prev
    })
  }

  const handleAvatarsSelect = () => {
    setAvatarsView(true)
    setModulesView(false)
    setUsersView(false)
    setPaymentsView(false)
    setPlanCardsView(false)
    setActivityView(false)
    setSearchParams((prev) => {
      prev.set('view', 'avatars')
      return prev
    })
  }

  const handleModulesSelect = () => {
    setModulesView(true)
    setAvatarsView(false)
    setUsersView(false)
    setPaymentsView(false)
    setPlanCardsView(false)
    setActivityView(false)
    setSearchParams((prev) => {
      prev.set('view', 'modules')
      return prev
    })
  }

  const handleUsersSelect = () => {
    setUsersView(true)
    setAvatarsView(false)
    setModulesView(false)
    setPaymentsView(false)
    setPlanCardsView(false)
    setActivityView(false)
    setSearchParams((prev) => {
      prev.set('view', 'users')
      return prev
    })
  }

  const handlePaymentsSelect = () => {
    setPaymentsView(true)
    setPlanCardsView(false)
    setActivityView(false)
    setAvatarsView(false)
    setModulesView(false)
    setUsersView(false)
    setSearchParams((prev) => {
      prev.set('view', 'payments')
      return prev
    })
  }

  const handlePlanCardsSelect = () => {
    setPlanCardsView(true)
    setActivityView(false)
    setPaymentsView(false)
    setAvatarsView(false)
    setModulesView(false)
    setUsersView(false)
    setSearchParams((prev) => {
      prev.set('view', 'plan-cards')
      return prev
    })
  }

  const handleActivitySelect = () => {
    setActivityView(true)
    setPlanCardsView(false)
    setPaymentsView(false)
    setAvatarsView(false)
    setModulesView(false)
    setUsersView(false)
    setSearchParams((prev) => {
      prev.set('view', 'activity')
      return prev
    })
  }

  const handleOrgViewSelect = (orgId: number) => {
    setAvatarsView(false)
    setModulesView(false)
    setUsersView(false)
    setPaymentsView(false)
    setPlanCardsView(false)
    setActivityView(false)
    setSelectedOrgId(orgId)
    setSearchParams((prev) => {
      prev.delete('view')
      prev.set('org', String(orgId))
      return prev
    })
  }

  const refreshOrganisations = async () => {
    try {
      const response = await authorizedGraphQLRequest<{ organisations: Organisation[] }>(GET_ORGANISATIONS)
      setOrganisations(response.organisations || [])
    } catch (error) {
      console.error('Failed to refresh organisations:', error)
    }
  }

  const refreshOrgDetails = async () => {
    if (!selectedOrgId) return

    try {
      setDetailsLoading(true)
      const response = await authorizedGraphQLRequest<{
        getOrganisationsWithSubscriptions: OrganisationWithDetails[]
      }>(GET_ORGANISATION_DETAILS, { organisationId: selectedOrgId })

      if (response.getOrganisationsWithSubscriptions?.length > 0) {
        setOrgDetails(response.getOrganisationsWithSubscriptions[0])
      }
    } catch (error) {
      console.error('Failed to refresh organisation details:', error)
    } finally {
      setDetailsLoading(false)
    }
  }

  const handleOrganisationDeleted = async () => {
    // Refresh organisations list
    await refreshOrganisations()
    // Clear selection and details
    setSelectedOrgId(null)
    setOrgDetails(null)
  }

  // Read from the list, not the details, so the tab set is right before details load.
  const isB2c = organisations.find((o) => o.id === selectedOrgId)?.kind === 'b2c'

  const tabs = [
    // A B2C organisation has no subscriptions: its modules are added directly.
    ...(isB2c
      ? []
      : [
          {
            value: 'subscriptions',
            label: 'Subscriptions',
            content: <SubscriptionsTab organisation={orgDetails} modules={modules} loading={detailsLoading} onRefresh={refreshOrgDetails} />,
            icon: <CreditCard size={16} />,
          },
        ]),
    {
      value: 'users',
      label: 'Users',
      content: <UsersTab organisation={orgDetails} loading={detailsLoading} onRefresh={refreshOrgDetails} />,
      icon: <Users size={16} />,
    },
    {
      value: 'modules',
      label: 'Modules',
      content: isB2c ? (
        <B2cModulesTab organisation={orgDetails} catalogue={modules} loading={detailsLoading} />
      ) : (
        <ModulesTab organisation={orgDetails} loading={detailsLoading} onRefresh={refreshOrgDetails} />
      ),
      icon: <Package size={16} />,
    },
    // What a B2C user subscribes to, and the minute packages plans and top-ups draw on.
    ...(isB2c
      ? [
          {
            value: 'plans',
            label: 'Plans',
            content: <PlansTab organisation={orgDetails} loading={detailsLoading} />,
            icon: <Sparkles size={16} />,
          },
        ]
      : []),
    {
      value: 'targets',
      label: 'Targets',
      content: <TargetsTab organisation={orgDetails} modules={modules} loading={detailsLoading} onRefresh={refreshOrgDetails} />,
      icon: <Target size={16} />,
    },
    {
      value: 'chats',
      label: 'Chats',
      content: <ChatsTab organisation={orgDetails} loading={detailsLoading} />,
      icon: <MessageSquare size={16} />,
    },
    {
      value: 'user-targets',
      label: 'User Targets',
      content: <UserTargetsTab organisation={orgDetails} loading={detailsLoading} />,
      icon: <BarChart3 size={16} />,
    },
    // What this organisation's users have bought. Read-only, and B2C only.
    ...(isB2c
      ? [
          {
            value: 'purchases',
            label: 'Purchases',
            content: <PurchasesTab organisation={orgDetails} loading={detailsLoading} />,
            icon: <Receipt size={16} />,
          },
        ]
      : []),
    {
      value: 'settings',
      label: 'Settings',
      content: <SettingsTab organisation={orgDetails} loading={detailsLoading} onRefresh={refreshOrgDetails} />,
      icon: <Settings size={16} />,
    },
    {
      value: 'danger',
      label: 'Danger',
      content: <DangerTab organisation={orgDetails} loading={detailsLoading} onRefresh={refreshOrgDetails} onOrganisationDeleted={handleOrganisationDeleted} />,
      icon: <AlertTriangle size={16} />,
    },
  ]

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        {/* Fixed Sidebar */}
        <OrganisationSidebar
          organisations={organisations}
          selectedOrgId={avatarsView || modulesView || usersView || paymentsView || planCardsView || activityView ? null : selectedOrgId}
          onSelect={handleOrgViewSelect}
          onOrganisationCreated={refreshOrganisations}
          avatarsSelected={avatarsView}
          onAvatarsSelect={handleAvatarsSelect}
          modulesSelected={modulesView}
          onModulesSelect={handleModulesSelect}
          usersSelected={usersView}
          onUsersSelect={handleUsersSelect}
          paymentsSelected={paymentsView}
          onPaymentsSelect={handlePaymentsSelect}
          planCardsSelected={planCardsView}
          onPlanCardsSelect={handlePlanCardsSelect}
          activitySelected={activityView}
          onActivitySelect={handleActivitySelect}
        />

        {/* Main Content - with left margin for fixed sidebar */}
        <div className="ml-72">
          <div className="p-6">
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl border-0 p-8 hover:shadow-2xl transition-all duration-300">
              {/* Header */}
              <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-primary to-primary/70 rounded-xl shadow-md">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">Dashboard</h2>
                    {orgDetails && (
                      <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
                        {orgDetails.name}
                        {/* Two organisations may share a name (one B2B, one B2C) and the tabs differ by kind. */}
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${orgDetails.kind === 'b2c' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}
                          title={
                            orgDetails.kind === 'b2c' ? 'Consumer organisation: people join through links and buy subscriptions' : 'Business organisation: users are created or attached by an admin'
                          }
                        >
                          {orgDetails.kind}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Content */}
              {avatarsView ? (
                <AvatarsTab />
              ) : modulesView ? (
                <ModulesView onModulesChanged={refreshModules} />
              ) : usersView ? (
                <UsersView />
              ) : paymentsView ? (
                <PaymentAccountsView />
              ) : planCardsView ? (
                <PlanCardsView />
              ) : activityView ? (
                <ActivityView />
              ) : selectedOrgId ? (
                <Tabs tabs={tabs} defaultValue={tabs.some((tab) => tab.value === activeTab) ? activeTab : tabs[0].value} onValueChange={handleTabChange} className="w-full" />
              ) : (
                <div className="text-center text-gray-500 py-12">
                  <Building2 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p>Select an organisation from the sidebar</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
})

export default DashboardPage
